import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  TextField,
  Typography,
} from "@mui/material";
import { Download } from "@mui/icons-material";
import TopNav from "../components/TopNav";
import { loadQuickbooksData } from "../api/quickbooks";
import {
  buildCustomerFiles,
  buildInvoiceFiles,
  buildProductFiles,
  Csv,
  QbCustomer,
  QbProduct,
  QbProposal,
  toCsv,
} from "../utils/quickbooksExport";
import { formatMoney } from "../utils";
import pageStyle from "./Quickbooks.module.css";

function readSetting(key: string, fallback: string) {
  try {
    return localStorage.getItem(key) || fallback;
  } catch {
    return fallback;
  }
}

function download(name: string, rows: Csv) {
  const url = URL.createObjectURL(new Blob([toCsv(rows)], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// yyyy-mm-dd from a date input -> local midnight
const parseDay = (s: string) => {
  if (!s) return undefined;
  const [y, m, d] = s.split("-").map(Number);
  return new Date(y, m - 1, d).getTime();
};

function Files({ base, files, noun }: { base: string; files: Csv[]; noun: string }) {
  return (
    <div className={pageStyle.buttons}>
      {files.map((f, i) => {
        const part = files.length > 1 ? `-part-${i + 1}-of-${files.length}` : "";
        return (
          <Button
            key={i}
            variant="contained"
            startIcon={<Download />}
            disabled={f.length < 2}
            onClick={() => download(`${base}${part}.csv`, f)}
          >
            {files.length > 1 ? `Part ${i + 1} of ${files.length}` : "Download"} ({f.length - 1} {noun})
          </Button>
        );
      })}
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: React.ReactNode }) {
  return (
    <section className={pageStyle.section}>
      <div className={pageStyle.stepHead}>
        <span className={pageStyle.step}>{n}</span>
        <Typography variant="h6">{title}</Typography>
      </div>
      <div className={pageStyle.body}>{children}</div>
    </section>
  );
}

function QuickbooksPage() {
  const [data, setData] = useState<{
    customers: QbCustomer[];
    products: QbProduct[];
    proposals: QbProposal[];
  }>();
  const [error, setError] = useState("");
  const [incomeAccount, setIncomeAccount] = useState(() => readSetting("qbIncomeAccount", "Services"));
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [includeArchived, setIncludeArchived] = useState(true);

  useEffect(() => {
    loadQuickbooksData()
      .then(setData)
      .catch((e) => setError(String(e)));
  }, []);

  const changeIncomeAccount = (v: string) => {
    setIncomeAccount(v);
    try {
      localStorage.setItem("qbIncomeAccount", v);
    } catch {
      // remembered for this visit only
    }
  };

  const productFiles = useMemo(
    () => (data ? buildProductFiles(data.products, incomeAccount || "Services") : []),
    [data, incomeAccount]
  );
  const customerFiles = useMemo(() => (data ? buildCustomerFiles(data.customers) : []), [data]);
  const invoices = useMemo(
    () =>
      data
        ? buildInvoiceFiles(data.proposals, data.customers, data.products, {
            from: parseDay(from),
            to: parseDay(to),
            includeArchived,
          })
        : undefined,
    [data, from, to, includeArchived]
  );
  const range = [from || "start", to || "today"].join("-to-");

  return (
    <div className="page">
      <TopNav pageTitle="QuickBooks" />
      <main className={pageStyle.main}>
        <div className={pageStyle.content}>
          <div>
            <Typography variant="h5">Export to QuickBooks Online</Typography>
            <Typography color="text.secondary">
              These files are laid out for QuickBooks Online's importer (Settings ⚙ › Import data). Import
              them in order: products, then customers, then invoices, because invoices must match
              customer and product names that already exist in QuickBooks.
            </Typography>
          </div>

          <Alert severity="info">
            <strong>Before importing invoices in QuickBooks:</strong>
            <ul className={pageStyle.list}>
              <li>
                Turn on <em>Custom transaction numbers</em> (Settings › Account and settings › Sales ›
                Sales form content) so QuickBooks keeps these invoice numbers. Importing the same file
                twice then shows up as duplicate numbers instead of double invoices.
              </li>
              <li>Pick the date format MM/DD/YYYY on the import screen.</li>
              <li>
                QuickBooks works out sales tax itself. After importing, compare its totals with the
                tax shown below.
              </li>
            </ul>
          </Alert>

          {error && <Alert severity="error">Couldn't read the CRM data: {error}</Alert>}
          {!data && !error && (
            <div className={pageStyle.loading}>
              <CircularProgress size={28} />
            </div>
          )}

          {data && invoices && (
            <>
              <Step n={1} title="Products and services">
                <Typography variant="body2" color="text.secondary">
                  {data.products.length} products, imported as Service items. QuickBooks needs an income
                  account for each one; type the name exactly as it appears in your QuickBooks chart of
                  accounts.
                </Typography>
                <TextField
                  size="small"
                  label="Income account in QuickBooks"
                  value={incomeAccount}
                  onChange={(e) => changeIncomeAccount(e.target.value)}
                  className={pageStyle.field}
                />
                <Files base="lifetree-products" files={productFiles} noun="products" />
              </Step>

              <Step n={2} title="Customers">
                <Typography variant="body2" color="text.secondary">
                  {data.customers.length} customers. People with the same name get their street added
                  (for example "John Smith (12 Maple Ave)"), because QuickBooks needs every customer
                  name to be different.
                </Typography>
                <Files base="lifetree-customers" files={customerFiles} noun="customers" />
              </Step>

              <Step n={3} title="Invoices">
                <Typography variant="body2" color="text.secondary">
                  Won and Done jobs, one row per line item, dated by invoice date (or the day the
                  proposal was made if it has none). QuickBooks takes at most 100 invoices and 1,000
                  rows per file, so larger exports come in parts. Import each part.
                </Typography>
                <div className={pageStyle.filters}>
                  <TextField
                    size="small"
                    type="date"
                    label="From"
                    value={from}
                    onChange={(e) => setFrom(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="To"
                    value={to}
                    onChange={(e) => setTo(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeArchived}
                        onChange={(e) => setIncludeArchived(e.target.checked)}
                      />
                    }
                    label="Include archived jobs"
                  />
                </div>
                <div className={pageStyle.summary}>
                  <div>
                    <span>Invoices</span>
                    <strong>{invoices.invoiceCount}</strong>
                  </div>
                  <div>
                    <span>Line rows</span>
                    <strong>{invoices.rowCount}</strong>
                  </div>
                  <div>
                    <span>Before tax</span>
                    <strong>{formatMoney(invoices.total)}</strong>
                  </div>
                  <div>
                    <span>Tax in the CRM</span>
                    <strong>{formatMoney(invoices.tax)}</strong>
                  </div>
                </div>
                {invoices.invoiceCount === 0 ? (
                  <Typography color="text.secondary">No Won or Done jobs in these dates.</Typography>
                ) : (
                  <Files base={`lifetree-invoices-${range}`} files={invoices.files} noun="rows" />
                )}
                {invoices.skipped.length > 0 && (
                  <Alert severity="warning">
                    <strong>Left out ({invoices.skipped.length}):</strong>
                    <ul className={pageStyle.list}>
                      {invoices.skipped.map((s) => (
                        <li key={s.id + s.message}>
                          <Link to={`/proposals?id=${s.id}`}>{s.customer}</Link> {s.message}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}
                {invoices.warnings.length > 0 && (
                  <Alert severity="info">
                    <strong>Check before importing ({invoices.warnings.length}):</strong>
                    <ul className={pageStyle.list}>
                      {invoices.warnings.map((w, i) => (
                        <li key={i}>
                          <Link to={`/proposals?id=${w.id}`}>{w.customer}</Link> {w.message}
                        </li>
                      ))}
                    </ul>
                  </Alert>
                )}
              </Step>
            </>
          )}
        </div>
      </main>
    </div>
  );
}

export default QuickbooksPage;
